import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";

import { useProjectRetrieval } from "@planzone/view-models";
import { Assignable } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { ScrollingText } from "@common/scrollingText";
import { TagList } from "./tagList";
import { ProjectPortfoliosList } from "./projectPortfoliosList";
import { ToggleIcon } from "./toggleIcon";
import { Loader } from "./loader";
import { Props } from "./types";
import styles from "./styles.scss";

function ProjectPopup({ projectId }: Props) {
  const { t } = useTranslation();
  const { project, isRequestPending, retrieveProject } =
    useProjectRetrieval(projectId);
  const { pushView } = useModal();

  React.useEffect(() => {
    !isRequestPending && retrieveProject(projectId);
  }, [projectId]);

  return (
    <div className={styles.projectPopup}>
      {isRequestPending ? (
        <Loader />
      ) : (
        <>
          <div className={styles.header}>
            <ScrollingText speed={100}>
              <h3 className={styles.name}>{project.name}</h3>
            </ScrollingText>
          </div>
          <div className={styles.dates}>
            {!!project.rootList.startDate && (
              <div className={styles.group}>
                <h3 className={styles.label}>
                  {t("projectDetails.startDateTitle")}:
                </h3>
                <p className={styles.date}>
                  {new Date(project.rootList.startDate).toLocaleDateString()}
                </p>
              </div>
            )}
            {!!project.rootList.endDate && (
              <div className={styles.group}>
                <h3 className={styles.label}>
                  {t("projectDetails.endDateTitle")}:
                </h3>
                <p className={styles.date}>
                  {new Date(project.rootList.endDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
          {!!project.description && (
            <p className={styles.description}>{project.description}</p>
          )}
          <div className={styles.group}>
            <h3 className={styles.subTitle}>
              {t("projectDetails.projectPortfoliosList")}:
            </h3>
            <ProjectPortfoliosList
              project={project}
              isRemovable
              remove={(portfolio) =>
                pushView({
                  key: UseCases.removeProjectFromPortfolio,
                  data: { portfolio, project },
                })
              }
            />
          </div>
          <div className={styles.group}>
            <h3 className={styles.subTitle}>{t("projectDetails.tagsList")}:</h3>
            <TagList
              tags={project.tags}
              project={project}
              id={project.uuid}
              type={Assignable.PROJECT}
            />
          </div>
        </>
      )}
    </div>
  );
}

export { ProjectPopup, ToggleIcon };
